import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Rating,
  TextField,
} from "@mui/material";
import { Product } from "../../../lib/types/product";
import ReviewService from "../../services/ReviewService";
import { sweetErrorHandling, sweetTopSuccessAlert } from "../../../lib/sweetAlert";
import { Messages } from "../../../lib/config";

interface ReviewModalProps {
  open: boolean;
  product: Product | null;
  onClose: () => void;
  onSubmitted?: () => void;
}

export default function ReviewModal(props: ReviewModalProps) {
  const { open, product, onClose, onSubmitted } = props;
  const [rating, setRating] = useState<number | null>(5);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    try {
      if (!product || !text.trim() || !rating) {
        throw new Error(Messages.error3);
      }
      setLoading(true);
      const service = new ReviewService();
      await service.createReview({
        productId: product._id,
        reviewText: text.trim(),
        reviewRating: rating,
      });
      await sweetTopSuccessAlert("Successfully submitted!", 1200);
      setText("");
      setRating(5);
      onClose();
      onSubmitted?.();
    } catch (err) {
      sweetErrorHandling(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
        Write a review{product ? ` · ${product.productName}` : ""}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Rating
            value={rating}
            onChange={(_, value) => setRating(value)}
            size="large"
          />
          <TextField
            multiline
            minRows={4}
            label="Your review"
            value={text}
            onChange={(e) => setText(e.target.value)}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={loading}
          onClick={submit}
          sx={{ bgcolor: "#8B2635", "&:hover": { bgcolor: "#6e1d2a" } }}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
